import {truncate} from 'lodash';
import React from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../../../../libs/shared/core/src/helpers/internationalization/message';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {
  ContactModifiers,
  contactModifierTitleDescriptor,
} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {trackSearchHintDropdownOpen} from '../../searchTrackingHelpers';
import {SearchHintItemPill} from '../searchHintItemPill';
import {ContactNameHandle} from '../searchModifiersHooks';
import {SearchHintContactDropdown} from './searchHintContactDropdown';

export interface SearchHintContactItemProps {
  selectedContacts: ReadonlyArray<ContactNameHandle>;
  isDisabled: boolean;
  onClick: (arg: string) => void;
  cellsContext?: CellsContext;
  initialSuggestions?: ReadonlyArray<ContactNameHandle>;
  queryHash?: string;
  modifier: ContactModifiers;
  onClear: () => void;
}

const StyledFlexDiv = styled.div`
  display: flex;
`;

export const SearchHintContact = (props: SearchHintContactItemProps) => {
  const {selectedContacts, isDisabled, queryHash, modifier, onClear} = props;

  const localize = useLocalize();

  const interactionId = `search-hint-${modifier}`;
  const isActive = selectedContacts.length > 0;

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <SearchHintItemPill
          isActive={isActive}
          isDisabled={isDisabled}
          isHovered={isOpen}
          interactionId={interactionId}
        >
          <StyledFlexDiv>{renderContactPillTitle(selectedContacts, modifier, localize, {})}</StyledFlexDiv>
        </SearchHintItemPill>
      )}
      onPopoverToggle={(isOpen) => {
        if (isOpen) {
          trackSearchHintDropdownOpen({
            queryHash,
            modifier,
          });
        }
      }}
      renderModal={(popoverProps) => (
        <SearchHintContactDropdown
          {...popoverProps}
          selectedContacts={selectedContacts}
          onClick={props.onClick}
          cellsContext={props.cellsContext}
          initialSuggestions={props.initialSuggestions}
          modifier={modifier}
          onClear={onClear}
        />
      )}
    />
  );
};

const longTruncationLength = 31;

function maybeTruncateString(value: string, maxChars: number): string {
  return truncate(value, {length: maxChars, omission: '…'});
}

export const renderContactPillTitle = (
  selectedContacts: ReadonlyArray<ContactNameHandle>,
  modifier: ContactModifiers,
  localize: Localizer,
  {
    displayModifierName = true,
    valueTruncationLimit = 16,
  }: {displayModifierName?: boolean; valueTruncationLimit?: number},
) => {
  const displayedHintValues = selectedContacts;

  const modifierTitle = localize(contactModifierTitleDescriptor(modifier));

  const or = localize(
    defineMessage({
      id: 'search-hint-or',
      message: 'or',
    }),
  );
  const modifierName = displayModifierName ? `${modifierTitle} ` : '';
  const contactsNames =
    displayedHintValues.length <= 2
      ? displayedHintValues
          .map(({name, handle}) => maybeTruncateString(name ?? handle, valueTruncationLimit))
          .join(` ${or} `)
      : `${maybeTruncateString(
          displayedHintValues[0].name ?? displayedHintValues[0].handle,
          valueTruncationLimit,
        )} ${or} +${displayedHintValues.length - 1}`;

  // Using a condition for now, we're definetly gonna have another type of key later on as we add more modifiers.
  const hintTitle = `${modifierName}${contactsNames}`;

  return maybeTruncateString(hintTitle, longTruncationLength);
};
